import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { FilePdf, IdentificationCard, ArrowRight, Plus } from "@phosphor-icons/react";
import { StatusPill } from "@/components/StatusPill";
import NewProposalModal from "@/components/NewProposalModal";
import NewPacketModal from "@/components/NewPacketModal";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={24} weight="bold" className="text-white" />
      </div>
      <div>
        <div className="text-2xl font-black text-slate-900">{value ?? "—"}</div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [openProposal, setOpenProposal] = useState(false);
  const [openPacket, setOpenPacket] = useState(false);
  const reload = () => api.get("/dashboard/manager").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { reload(); }, []);

  return (
    <>
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="mb-6">
        <div className="tinylabel">Overview</div>
        <h1 className="text-3xl font-black">My Dashboard</h1>
        <p className="text-slate-500 mt-1 text-sm">Welcome back, {user?.email}.</p>
      </div>

      {/* Quick action buttons */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button data-testid="manager-new-proposal-btn"
          onClick={() => setOpenProposal(true)}
          className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center gap-2 transition-colors">
          <Plus size={18} weight="bold" /> New Proposal
        </button>
        <button data-testid="manager-new-packet-btn"
          onClick={() => setOpenPacket(true)}
          className="h-11 px-5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg flex items-center gap-2 transition-colors">
          <Plus size={18} weight="bold" /> New Packet
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <StatCard icon={FilePdf} label="Total Proposals" value={data?.proposals_total} color="bg-blue-500" />
        <StatCard icon={IdentificationCard} label="Total Packets" value={data?.packets_total} color="bg-teal-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Proposals */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-bold text-slate-800">Recent Proposals</span>
            <Link to="/proposals" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold">
              View all <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {!data ? (
            <div className="px-4 py-8 text-slate-400 text-sm text-center">Loading…</div>
          ) : data.recent_proposals.length === 0 ? (
            <div className="px-4 py-8 text-slate-400 text-sm text-center">No proposals created yet.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.recent_proposals.map((p) => (
                  <tr key={p.id} className="border-t border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{p.company_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{p.contact_name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <StatusPill status={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent Packets */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-bold text-slate-800">Recent Packets</span>
            <Link to="/packets" className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1 font-semibold">
              View all <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {!data ? (
            <div className="px-4 py-8 text-slate-400 text-sm text-center">Loading…</div>
          ) : data.recent_packets.length === 0 ? (
            <div className="px-4 py-8 text-slate-400 text-sm text-center">No packets created yet.</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.recent_packets.map((pk) => (
                  <tr key={pk.id} className="border-t border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{pk.worker_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{pk.company_name || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <StatusPill status={pk.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>

    {openProposal && <NewProposalModal onClose={() => setOpenProposal(false)} onCreated={() => { setOpenProposal(false); reload(); }} />}
    {openPacket   && <NewPacketModal   onClose={() => setOpenPacket(false)}   onCreated={() => { setOpenPacket(false);   reload(); }} />}
    </>
  );
}
